import {
  KeyboardCode, KeyboardSensor, pointerWithin, type ClientRect, type Collision, type CollisionDetection,
  type DroppableContainer, type KeyboardCoordinateGetter, type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  insertBlock, locateBlock, locatePage, moveBlockTo, type BlockPlace,
} from '@lib/customization/reportDesign';
import {
  reportBlockTypes, reportDesignLimits, type ReportBlock, type ReportBlockType, type ReportDesignContent,
} from '@store/api/reportDesignEndpoints';

/**
 * Dragging blocks on the canvas, as functions: what a drag carries and what it is over, where
 * a drop would put it, and the one edit a drop comes to. Nothing here draws or keeps state, so
 * every rule of a drag can be read, and run, by itself.
 */

/**
 * The ids the drag context knows things by. What an id starts with tells a block from a page
 * from a palette entry: a preset calls a block `hero`, which is also what the palette's entry
 * for that type would be called.
 */
export const dragIds = {
  block: (blockId: string): string => `block:${blockId}`,
  page: (pageId: string): string => `page:${pageId}`,
  foot: (pageId: string): string => `foot:${pageId}`,
  palette: (type: ReportBlockType): string => `palette:${type}`,
  /** The column the sheets stand in. Nothing is dropped on it (see `underPointer`). */
  desk: 'desk',
};

/** A block on its page, as the canvas describes it to the drag context. */
export interface BlockPart {
  kind: 'block';
  blockId: string;
  pageId: string;
  index: number;
}

/** What a drag carries: a block of the design, or a block type out of the palette. */
export type DragSource = BlockPart | { kind: 'palette'; type: ReportBlockType };

/** What a drag can be over: a block, the foot of a page's blocks, or a page as a whole. */
export type DropTarget = BlockPart | { kind: 'foot' | 'page'; pageId: string };

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isBlockPart = (data: Record<string, unknown>): boolean =>
  data.kind === 'block' && typeof data.blockId === 'string' && typeof data.pageId === 'string' && typeof data.index === 'number';

/** The `data` of what is dragged, read as what it carries. The context hands it over untyped. */
export const sourceOf = (data: unknown): DragSource | null => {
  if (!isRecord(data)) return null;
  if (isBlockPart(data)) return data as unknown as BlockPart;

  return data.kind === 'palette' && reportBlockTypes.some((type) => type === data.type)
    ? { kind: 'palette', type: data.type as ReportBlockType }
    : null;
};

/** The `data` of a droppable, read as what a drag is over. */
export const targetOf = (data: unknown): DropTarget | null => {
  if (!isRecord(data)) return null;
  if (isBlockPart(data)) return data as unknown as BlockPart;

  return (data.kind === 'foot' || data.kind === 'page') && typeof data.pageId === 'string'
    ? { kind: data.kind, pageId: data.pageId }
    : null;
};

/**
 * Where a drop would put what is dragged: in front of the block that is at `before` on the
 * page now, or after its last block when `before` is their number. It is where the canvas
 * draws its line.
 */
export interface Placement {
  pageId: string;
  before: number;
}

/**
 * Where a drag would land, given what it is over and whether it stands past the middle of
 * that (`after`). `null` where a drop would change nothing: over nothing, over a page that is
 * full, or on either side of the place the block already has.
 *
 * The draft is asked where the block that is under the drag stands, not the drag context,
 * whose description of it can be a render old.
 */
export const placementOf = (
  content: ReportDesignContent, source: DragSource | null, target: DropTarget | null, after: boolean,
): Placement | null => {
  if (!source || !target) return null;

  const over = target.kind === 'block' ? locateBlock(content, target.blockId) : null;
  const page = over?.page ?? (target.kind === 'block' ? undefined : locatePage(content, target.pageId)?.page);

  if (!page) return null;

  // A page stands for its start, and for its end when the drag is past its middle. Its foot is its end.
  let before = target.kind === 'foot' || after ? page.blocks.length : 0;

  if (over) before = over.index + (after ? 1 : 0);

  if (source.kind === 'block') {
    const from = locateBlock(content, source.blockId);

    if (!from) return null;

    if (from.page.id === page.id) {
      return before === from.index || before === from.index + 1 ? null : { pageId: page.id, before };
    }
  }

  return page.blocks.length >= reportDesignLimits.blocksPerPage ? null : { pageId: page.id, before };
};

/**
 * The place a block has once it is dropped at a placement. Taken off its own page first, it
 * leaves a place less in front of the one it goes to.
 */
export const landingOf = (content: ReportDesignContent, source: DragSource, place: Placement): BlockPlace => {
  const from = source.kind === 'block' ? locateBlock(content, source.blockId) : null;
  const shifted = from?.page.id === place.pageId && place.before > from.index;

  return { pageId: place.pageId, index: shifted ? place.before - 1 : place.before };
};

/**
 * The one edit a drop comes to. `block` is what a palette entry puts down: minted by the
 * caller, which selects it afterwards.
 */
export const dropOn = <D extends ReportDesignContent>(draft: D, source: DragSource, place: Placement, block?: ReportBlock): D => {
  const { pageId, index } = landingOf(draft, source, place);

  if (source.kind === 'block') return moveBlockTo(draft, source.blockId, pageId, index);

  return block ? insertBlock(draft, pageId, block, index) : draft;
};

// ---- What a drag is over ----

type CollisionArgs = Parameters<CollisionDetection>[0];

const middleOf = (rect: ClientRect): number => rect.top + rect.height / 2;

/** One collision: what the drag is over, and which side of it the drag stands on. */
const standingOn = (container: DroppableContainer, after: boolean, value = 0): Collision[] =>
  [{ id: container.id, data: { droppableContainer: container, value, after } }];

/**
 * A pointer: the drag is over the sheet the pointer is on, and there over the block under it.
 * On none of that page's blocks (in a margin, or in the room a centred or a short page leaves)
 * it is over the one nearest in height, so that the whole sheet takes a drop and above the
 * first block means before it. Only a page without blocks is itself what the drag is over.
 *
 * The sheet is found first and its blocks alone are asked: a block is never what a drag is
 * over while the pointer is on another page's sheet, wherever its rectangle reaches.
 *
 * Off every sheet, between two of them or on the results, the drag is over the desk. Nothing
 * lands there, but the context scrolls what the drag is over, and a palette entry is not on
 * the canvas itself: carried to its edge, it would scroll the palette instead.
 */
const underPointer = (args: CollisionArgs, y: number): Collision[] => {
  const { droppableContainers, droppableRects } = args;

  const hits = pointerWithin(args).flatMap(({ id }) => droppableContainers.find((container) => container.id === id) ?? []);
  const sheet = hits.find((container) => targetOf(container.data.current)?.kind === 'page');
  const page = targetOf(sheet?.data.current);

  if (!sheet || !page) {
    const desk = hits.find((container) => container.id === dragIds.desk);

    return desk ? standingOn(desk, false) : [];
  }

  const onPage = (container: DroppableContainer): boolean => {
    const part = targetOf(container.data.current);

    return part !== null && part.kind !== 'page' && part.pageId === page.pageId;
  };

  let over = hits.find(onPage);

  if (!over) {
    let nearest = Infinity;

    for (const container of droppableContainers) {
      const rect = droppableRects.get(container.id);

      if (!rect || !onPage(container) || targetOf(container.data.current)?.kind !== 'block') continue;

      const away = Math.max(rect.top - y, y - rect.bottom, 0);

      if (away < nearest) {
        nearest = away;
        over = container;
      }
    }
  }

  over ??= sheet;

  const rect = droppableRects.get(over.id);

  return standingOn(over, rect ? y > middleOf(rect) : false);
};

/** A droppable a block can be carried onto from the keyboard, with where it is. */
interface Station {
  container: DroppableContainer;
  target: DropTarget;
  rect: ClientRect;
}

/**
 * Where the keyboard can carry a block, from the top of the canvas down: every block, to stand
 * before, and the foot of every page, for its end. A page as a whole is not among them: it
 * says nothing its first block and its foot do not.
 *
 * From the top down is the order of the design, because a sheet is as tall as what is on it:
 * no block of one page lies beside a block of another.
 */
const stationsOf = (containers: DroppableContainer[], rects: Map<UniqueIdentifier, ClientRect>): Station[] =>
  containers.flatMap((container) => {
    const rect = rects.get(container.id);
    const target = targetOf(container.data.current);

    return rect && target && target.kind !== 'page' ? [{ container, target, rect }] : [];
  }).sort((a, b) => a.rect.top - b.rect.top);

/** The station the carried rectangle lies on: the one whose top edge is nearest to its own. */
const stationAt = (stations: Station[], carried: ClientRect): Station | undefined =>
  stations.reduce<Station | undefined>((nearest, station) => (
    !nearest || Math.abs(station.rect.top - carried.top) < Math.abs(nearest.rect.top - carried.top) ? station : nearest
  ), undefined);

/**
 * The keyboard: there is no pointer, only the rectangle of the block that is carried, which
 * `keyboardStep` lays with its top on the top of a station. That station is what the drag is
 * over, and the drag stands before it. Only a block is carried this way.
 */
const underCarriedRect = ({ active, collisionRect, droppableContainers, droppableRects }: CollisionArgs): Collision[] => {
  if (sourceOf(active.data.current)?.kind !== 'block') return [];

  const station = stationAt(stationsOf(droppableContainers, droppableRects), collisionRect);

  return station ? standingOn(station.container, false) : [];
};

/**
 * What a drag is over, for the drag context.
 *
 * With a pointer it is what the pointer is in, not what the dragged block's centre is nearest
 * to: a sheet is large, its blocks run from a hairline to a third of a page, and the block in
 * the overlay can reach far from the pointer that holds it. The keyboard has no pointer and
 * goes by the carried rectangle.
 *
 * Each collision says besides which side of its droppable the drag stands on (`sideOf`).
 */
export const collide: CollisionDetection = (args) =>
  (args.pointerCoordinates ? underPointer(args, args.pointerCoordinates.y) : underCarriedRect(args));

/**
 * Whether a drag stands past the middle of what it is over, as `collide` left it on that
 * collision. `null` when the collisions are not of the moment `overId` is of: the context
 * reports a move with the collisions it has just found and what it was over a render ago.
 */
export const sideOf = (overId: UniqueIdentifier | undefined, collisions: Collision[] | null): 'before' | 'after' | null => {
  const [first] = collisions ?? [];

  if (first?.id !== overId) return null;

  return first?.data?.after === true ? 'after' : 'before';
};

/**
 * Where an arrow key carries a block: onto the next station up or down, one place of the
 * design a press, across pages too.
 *
 * In place of the sortable preset's getter, which chooses by the corners of the carried
 * rectangle and so by its size. Blocks here are of very different heights: with that getter a
 * tall block carried down past short ones never left its page (it stayed over its own place,
 * or went back and forth between two others), and a block carried up went from sheet to
 * sheet past every place in between.
 *
 * A station where a drop would change nothing is passed over, so that every press shows:
 * the block after the carried one and, for the last block of a page, that page's foot, which
 * are both its own place, and a page that is full.
 */
export const keyboardStep: KeyboardCoordinateGetter = (event, { context }) => {
  const by = { [KeyboardCode.Down]: 1, [KeyboardCode.Up]: -1 }[event.code];
  const { active, collisionRect, droppableContainers, droppableRects } = context;
  const source = sourceOf(active?.data.current);

  if (!by || !collisionRect || source?.kind !== 'block') return undefined;

  // The arrow keys carry the block now: the page is not to scroll by them as well.
  event.preventDefault();

  const stations = stationsOf(droppableContainers.getEnabled(), droppableRects);
  const onPage = (pageId: string) => stations.filter(({ target }) => target.kind === 'block' && target.pageId === pageId).length;
  const last = onPage(source.pageId) - 1;

  const changesNothing = ({ target }: Station): boolean => {
    if (target.pageId !== source.pageId) return onPage(target.pageId) >= reportDesignLimits.blocksPerPage;

    return target.kind === 'block' ? target.index === source.index + 1 : source.index === last;
  };

  const here = stationAt(stations, collisionRect);

  if (!here) return undefined;

  for (let index = stations.indexOf(here) + by; index >= 0 && index < stations.length; index += by) {
    const station = stations[index];

    if (!changesNothing(station)) return { x: station.rect.left, y: station.rect.top };
  }

  return undefined;
};

// ---- Listeners ----

const keyboardEvents: ReadonlySet<string> = new Set(KeyboardSensor.activators.map((activator) => activator.eventName));

/**
 * A draggable's listeners in two: the pointer's, for the whole of what is dragged, and the
 * keyboard's, for its grip alone. With both on the body, Enter on a block or on a palette
 * entry would pick it up, where it is meant to select the one and add the other.
 */
export const splitListeners = <T extends Record<string, unknown>>(listeners: T | undefined): { pointer: Partial<T>; keyboard: Partial<T> } => {
  const entries = Object.entries(listeners ?? {});

  return {
    pointer: Object.fromEntries(entries.filter(([event]) => !keyboardEvents.has(event))) as Partial<T>,
    keyboard: Object.fromEntries(entries.filter(([event]) => keyboardEvents.has(event))) as Partial<T>,
  };
};
